import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, Cloud, CheckCircle, AlertCircle } from 'lucide-react';
import profileBackup from '../services/profileBackup';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI, { generatePublicLinkId } from '../services/api';
import { extractTextFromDocx, parseResumeText } from '../utils/resumeTextParser';
import { extractTextFromPDF } from '../services/pdfParser';

/**
 * Backup Controls Component
 * - Auto-save toggle (local)
 * - Export/Import backups
 */
const BackupControls = ({
  selectedResumeId = null,
  showExport = true,
  showImport = true,
  showAutoSave = true,
  showHeader = true,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('');
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [importMode, setImportMode] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importFileBase64, setImportFileBase64] = useState('');
  const [importFileType, setImportFileType] = useState('');

  useEffect(() => {
    setAutoSaveEnabled(profileBackup.isAutoSaveEnabled());
    setLastSync(profileBackup.getLastSyncTime());
  }, []);


  const handleExport = async (type) => {
    setLoading(true);
    setStatus(`Exporting ${type.toUpperCase()}...`);
    try {
      const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || fallbackApiUrl;
      const getToken = () => {
        try {
          const localUser = JSON.parse(localStorage.getItem('user') || '{}');
          const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
          return (
            user?.tokens?.idToken ||
            user?.idToken ||
            localUser?.tokens?.idToken ||
            localUser?.idToken ||
            sessionUser?.tokens?.idToken ||
            sessionUser?.idToken ||
            ''
          );
        } catch {
          return user?.tokens?.idToken || user?.idToken || '';
        }
      };
      const token = getToken();
      const base = AWS_API_BASE_URL.replace(/\/+$/, '');
      const queryParams = new URLSearchParams();
      queryParams.set('export', type);
      if (selectedResumeId) queryParams.set('resume_id', selectedResumeId);
      const url = `${base}/me/profile?${queryParams.toString()}`;
      const res = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        throw new Error(`Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const contentDisposition = res.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename="([^"]+)"/i);
      const filename = match?.[1] || `resume.${type === 'pdf' ? 'pdf' : 'docx'}`;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      setStatus('✅ Exported successfully!');
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      const message = error?.message || 'Export failed';
      const detail = message === 'Failed to fetch'
        ? 'Network error. Please check your connection or try again.'
        : message;
      setStatus(`❌ Export failed: ${detail}`);
      setTimeout(() => setStatus(''), 5000);
    } finally {
      setLoading(false);
      setShowExportOptions(false);
    }
  };

  const handleOpenImport = (mode) => {
    setImportMode(mode);
    setShowImportOptions(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        const base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setStatus('Analyzing file...');
    setImportFileName(file.name);

    try {
      let text = '';
      const lower = file.name.toLowerCase();
      const isDocx = lower.endsWith('.docx');

      if (lower.endsWith('.pdf')) {
        text = await extractTextFromPDF(file);
        setImportFileType('pdf');
        setImportFileBase64('');
      } else if (isDocx) {
        const [extractedText, base64] = await Promise.all([
          extractTextFromDocx(file),
          readFileAsBase64(file)
        ]);
        text = extractedText;
        setImportFileType('docx');
        setImportFileBase64(base64);
      } else {
        throw new Error('Unsupported file type. Please upload a PDF or DOCX.');
      }

      const parsed = parseResumeText(text);
      setImportPreview(parsed);
      setStatus('');
    } catch (error) {
      setStatus(`❌ Import failed: ${error.message}`);
      setTimeout(() => setStatus(''), 5000);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview) return;
    setLoading(true);
    setStatus('Creating new resume...');
    try {
      let newId;

      if (importFileType === 'docx' && importFileBase64) {
        const result = await resumeAPI.importResumeDocx({
          docxBase64: importFileBase64,
        });
        newId = result?.resume?.resume_id || result?.resume?.id || result?.resume?._id;
      } else {
        const resumeData = {
          ...importPreview,
          public_link_id: generatePublicLinkId(),
          is_primary: false,
          import_source: 'file',
          imported_at: new Date().toISOString(),
        };
        const created = await resumeAPI.create(resumeData);
        newId = created.id || created._id;
      }

      setStatus('✅ Imported successfully!');
      setImportPreview(null);
      setImportFileName('');
      setImportFileBase64('');
      setImportFileType('');
      if (newId) {
        navigate(`/builder/${newId}`);
      }
    } catch (error) {
      setStatus(`❌ Import failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = () => {
    setImportPreview(null);
    setImportFileName('');
    setImportFileBase64('');
    setImportFileType('');
  };

  const handleToggleAutoSave = () => {
    const newState = !autoSaveEnabled;
    profileBackup.setAutoSave(newState);
    setAutoSaveEnabled(newState);
    setStatus(newState ? '✅ Auto-save enabled!' : 'Auto-save disabled');
    setTimeout(() => setStatus(''), 3000);
  };


  const actionCount = [showExport, showImport].filter(Boolean).length;
  const actionGridClass = actionCount <= 1 ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Backup & Sync Controls</h3>
          {lastSync && (
            <span className="text-sm text-gray-500">
              Last sync: {lastSync.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {/* Status Message */}
      {status && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          status.includes('✅') ? 'bg-green-50 text-green-800' : 
          status.includes('❌') ? 'bg-red-50 text-red-800' : 
          'bg-blue-50 text-blue-800'
        }`}>
          {status.includes('✅') ? <CheckCircle className="w-5 h-5" /> : 
           status.includes('❌') ? <AlertCircle className="w-5 h-5" /> : 
           <Cloud className="w-5 h-5" />}
          <span className="text-sm font-medium">{status}</span>
        </div>
      )}

      {showAutoSave && (
        <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={handleToggleAutoSave}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">
              Enable Auto-Save (saves every 2 seconds)
            </span>
          </label>
          <p className="mt-2 text-xs text-gray-600 ml-7">
            Automatically saves your changes locally
          </p>
        </div>
      )}

      {/* Action Buttons */}
      {actionCount > 0 && (
        <div className={actionGridClass}>
          {showExport && (
            <div className="relative">
              <button
                onClick={() => setShowExportOptions((prev) => !prev)}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm font-medium">Export File</span>
              </button>
              {showExportOptions && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50"
                  >
                    Export as PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport('docx')}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50"
                  >
                    Export as Word (DOCX)
                  </button>
                </div>
              )}
            </div>
          )}

          {showImport && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowImportOptions((prev) => !prev)}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">Import File</span>
              </button>
              {showImportOptions && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleOpenImport('pdf')}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50"
                  >
                    Import PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenImport('docx')}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50"
                  >
                    Import Word (DOCX)
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={importMode === 'pdf' ? '.pdf' : importMode === 'docx' ? '.docx' : '.pdf,.docx'}
                onChange={handleImport}
                className="hidden"
                disabled={loading}
              />
            </div>
          )}
        </div>
      )}

      {(showExport || showImport) && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600">
            {showExport && (
              <>
                <strong>Export File:</strong> Download a Word (DOCX) or PDF resume
                {selectedResumeId ? ' (uses your selected resume)' : ' (uses your primary resume)'}
              </>
            )}
            {showExport && showImport && <br/>}
            {showImport && (
              <>
                <strong>Import File:</strong> Upload a Word or PDF resume to create a new resume
              </>
            )}
          </p>
        </div>
      )}

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Import Resume</h4>
            <p className="text-sm text-gray-600 mb-4">
              We parsed <strong>{importFileName || 'your file'}</strong>. Please confirm to create a new resume.
            </p>
            <div className="space-y-2 text-sm text-gray-700">
              <div><strong>Name:</strong> {importPreview.title || 'Untitled'}</div>
              {importPreview.headline && <div><strong>Headline:</strong> {importPreview.headline}</div>}
              {(importPreview.professional_summary || importPreview.summary) && (
                <div>
                  <strong>Summary:</strong>{' '}
                  {(importPreview.professional_summary || importPreview.summary).slice(0, 160)}
                  {(importPreview.professional_summary || importPreview.summary).length > 160 ? '…' : ''}
                </div>
              )}
              <div>
                <strong>Experience:</strong> {Array.isArray(importPreview.experience) ? importPreview.experience.length : 0} entries
              </div>
              <div>
                <strong>Education:</strong> {Array.isArray(importPreview.education) ? importPreview.education.length : 0} entries
              </div>
              <div>
                <strong>Skills:</strong> {Array.isArray(importPreview.skills) ? importPreview.skills.length : 0} items
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleCancelImport}
                className="btn btn-outline"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmImport}
                className="btn btn-primary"
                disabled={loading}
              >
                Create Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupControls;
